import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';
import {skipToken} from '@reduxjs/toolkit/query';
import {showNotification} from 'platform/components';

import {useCallback, useState} from 'react';
import {useDispatch, useStore} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {
  AuctionVehicleDetailResponseBody,
  AuctionVehiclesFilterRequestBody,
  getAuctionId,
  GetAuctionVehicleListApiArg,
  GetAuctionVehicleListApiResponse,
  ROWS_PER_PAGE,
  sourcingAuctionApi,
  useGetSourcingAuctionListQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {useSourcingListPoller} from './useSourcingListPoller';

// Per-tick poll page cap. See README §Real-time polling.
const MAX_POLL_PAGES = 5;

type UseAuctionsVehicleListQueriesProps = {
  filterBody: AuctionVehiclesFilterRequestBody | null;
  orderBy: GetAuctionVehicleListApiArg['orderBy'];
  currency: GetAuctionVehicleListApiArg['currency'];
  isFiltersOpen: boolean;
  isRealTime: boolean;
  page: number;
  pollingInterval: number;
  skip?: boolean;
};

export function useAuctionsVehicleListQueries(props: UseAuctionsVehicleListQueriesProps) {
  const {data, currentData, isLoading, isFetching, isError, error} = useGetSourcingAuctionListQuery(
    isNotNil(props.filterBody)
      ? {
          body: props.filterBody,
          page: props.page,
          orderBy: props.orderBy,
          currency: props.currency,
        }
      : skipToken,
    {skip: props.skip || props.isFiltersOpen}
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- concrete RootState lives in the consuming app; matches the State/Extra typing used by `sourcingApiHelpers.ts` and `handleIncomingData.ts`.
  const dispatch = useDispatch<ThunkDispatch<any, any, UnknownAction>>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- same RootState reasoning as the dispatch type above.
  const store = useStore<any>();
  const [pollingFulfilledTimeStamp, setPollingFulfilledTimeStamp] = useState<number | undefined>(
    undefined
  );
  const [isPolling, setIsPolling] = useState(false);

  const {filterBody, currency, orderBy} = props;

  const reset = () => {
    if (isNil(filterBody)) {
      return;
    }

    const firstPageQueryArgs: GetAuctionVehicleListApiArg = {
      body: filterBody,
      page: 1,
      orderBy,
      currency,
    };

    dispatch(
      sourcingAuctionApi.util.updateQueryData(
        'getSourcingAuctionList',
        firstPageQueryArgs,
        (draft) => {
          draft.data = [];
          draft.links = {
            ...draft.links,
            offset: 0,
            size: 0,
          };
          draft.scrollAttemptedOffset = undefined;
          draft.isEndReached = undefined;
        }
      )
    );
    dispatch(
      sourcingAuctionApi.endpoints.getSourcingAuctionList.initiate(firstPageQueryArgs, {
        forceRefetch: true,
        subscribe: false,
      })
    );
  };

  const canPoll =
    !props.skip &&
    !props.isFiltersOpen &&
    props.isRealTime &&
    isNotNil(filterBody) &&
    isNotNil(currency);

  // useCallback for correctness (poller interval dep), not perf.
  const doPoll = useCallback(async () => {
    if (isNil(filterBody) || isNil(currency)) {
      return;
    }
    const pollArgsBase = {body: filterBody, currency, orderBy};
    const mainCacheKeyArgs: GetAuctionVehicleListApiArg = {...pollArgsBase, page: 1};

    // Snapshot cached ids to detect overlap while walking poll pages. Empty cache → bail.
    const cachedItems =
      sourcingAuctionApi.endpoints.getSourcingAuctionList.select(mainCacheKeyArgs)(store.getState())
        .data?.data ?? [];
    if (cachedItems.length === 0) {
      return;
    }
    const seenIds = new Set(cachedItems.map(getAuctionId));

    const collected: AuctionVehicleDetailResponseBody[] = [];
    let overlapFound = false;
    let endOfFeed = false;

    setIsPolling(true);
    try {
      for (let pageNum = 1; pageNum <= MAX_POLL_PAGES && !overlapFound && !endOfFeed; pageNum++) {
        const subscription = dispatch(
          sourcingAuctionApi.endpoints.getSourcingAuctionListPoll.initiate(
            {...pollArgsBase, page: pageNum},
            {forceRefetch: true}
          )
        );
        try {
          const result = await subscription.unwrap();
          for (const vehicle of result.data) {
            const id = getAuctionId(vehicle);
            if (seenIds.has(id)) {
              overlapFound = true;
              break;
            }
            seenIds.add(id);
            collected.push(vehicle);
          }
          if (result.data.length < ROWS_PER_PAGE) {
            endOfFeed = true;
          }
        } finally {
          subscription.unsubscribe();
        }
      }

      if (collected.length > 0) {
        dispatch(
          sourcingAuctionApi.util.updateQueryData(
            'getSourcingAuctionList',
            mainCacheKeyArgs,
            (draft: GetAuctionVehicleListApiResponse) => {
              draft.data.unshift(...collected);
              if (draft.links) {
                draft.links.size = draft.data.length;
              }
            }
          )
        );
      }
      // Cap hit: more new vehicles than MAX_POLL_PAGES could cover. See README §Real-time polling.
      if (!overlapFound && !endOfFeed) {
        showNotification.warning(i18n.t('page.sourcing.notifications.realTimeUpdateTruncated'));
      }
      setPollingFulfilledTimeStamp(Date.now());
    } catch {
      // Next tick will retry.
    } finally {
      setIsPolling(false);
    }
  }, [dispatch, store, filterBody, currency, orderBy]);

  useSourcingListPoller({
    enabled: canPoll,
    pollingInterval: props.pollingInterval,
    doPoll,
  });

  return {
    fulfilledTimeStamp: pollingFulfilledTimeStamp,
    isPolling,
    data,
    currentData,
    scrollAttemptedOffset: currentData?.scrollAttemptedOffset,
    isEndReached: currentData?.isEndReached,
    reset,
    isLoading,
    isFetching,
    isError,
    error,
  };
}
